import {
  actionCardShownProps,
  actionCardViewedProps,
  buildActionCardProps,
  type ActionCardEventProps,
  type ActionCardAnalyticsContext,
  type ActionCardFormFactor,
  type ActionCardSurface,
  type CardWatchSuggestion,
} from '@littlebird/action-cards';

import { actionCardAnalyticsContext } from '@/features/action-cards/analytics';
import { trackEvent } from '@/utils/analytics';

import type { ActionCard, ActionCardPlacement } from '@littlebird/sdk';

export type WebSurface = 'web_chat' | 'web_home' | 'web_feeds';

const REPORTED_SURFACES: Record<WebSurface, ActionCardSurface> = {
  web_chat: 'chat',
  web_home: 'home',
  web_feeds: 'web_feeds',
};

export const reportedSurfaceOf = (surface: WebSurface): ActionCardSurface =>
  REPORTED_SURFACES[surface];

const trackCardEvent = (
  event: Parameters<typeof trackEvent>[0],
  card: ActionCard,
  props: Parameters<typeof buildActionCardProps>[1],
  eventProps: Record<string, unknown> = {}
): void =>
  trackEvent(event, {
    ...card.analytics,
    ...buildActionCardProps(card, props),
    ...eventProps,
  });

// The row a card took when Home or the Feed first showed it, counted from 0 at the top, and the list's length
export const placementProps = (placement: ActionCardPlacement | undefined) =>
  placement
    ? { position: placement.position, list_size: placement.listSize }
    : {};

export const actionCardEventSnapshot = (
  card: ActionCard,
  context: ActionCardAnalyticsContext
): ActionCardEventProps => ({
  ...card.analytics,
  ...buildActionCardProps(card, context),
});

export const trackCardShown = (
  card: ActionCard,
  surface: ActionCardSurface,
  formFactor: ActionCardFormFactor,
  presentation?: Omit<ActionCardAnalyticsContext, 'surface' | 'retry'>,
  placement?: ActionCardPlacement
): void => {
  const context = actionCardAnalyticsContext(surface, {
    ...presentation,
    form_factor: formFactor,
  });
  const props = actionCardShownProps(card, context, formFactor);
  if (!props) return;
  trackEvent('ACTION_CARD_SHOWN', {
    ...card.analytics,
    ...props,
    ...placementProps(placement),
  });
};

export const trackCardViewed = (
  card: ActionCard,
  surface: ActionCardPlacement['surface'],
  placement: ActionCardPlacement | undefined
): void => {
  const props = actionCardViewedProps(
    card,
    actionCardAnalyticsContext(surface)
  );
  if (!props) return;
  trackEvent('ACTION_CARD_VIEWED', {
    ...card.analytics,
    ...props,
    ...placementProps(placement),
  });
};

export const trackCardOpened = (
  card: ActionCard,
  surface: ActionCardSurface,
  formFactor: ActionCardFormFactor
): void => {
  const context = actionCardAnalyticsContext(surface, {
    form_factor: formFactor,
    card_expanded: true,
  });
  trackCardEvent('ACTION_CARD_OPENED', card, context);
};

// Read when pressed: a run of a failed card is the retry button
export const trackCardRun = (
  card: ActionCard,
  surface: ActionCardSurface,
  context = actionCardAnalyticsContext(surface)
): void =>
  trackCardEvent(
    'ACTION_CARD_CONFIRM_SUBMITTED',
    card,
    {
      ...context,
      retry: card.status === 'failed',
    },
    { action_intent: 'external_execution' }
  );

export const trackCardChatStarted = (
  card: ActionCard,
  surface: ActionCardSurface,
  context = actionCardAnalyticsContext(surface)
): void =>
  trackCardEvent('ACTION_CARD_CONFIRM_SUBMITTED', card, context, {
    action_intent: 'chat_handoff',
  });

export const trackCardChatClicked = (
  card: ActionCard,
  surface: ActionCardSurface,
  chatAction: 'cta' | 'take_over' | 'open' | 'prompt',
  context = actionCardAnalyticsContext(surface)
): void =>
  trackEvent('ACTION_CARD_CHAT_CLICKED', {
    ...card.analytics,
    ...buildActionCardProps(card, context),
    chat_action: chatAction,
  });

export const trackCardCompletedSnapshot = (
  props: ActionCardEventProps,
  completionMethod:
    | 'acknowledge'
    | 'run'
    | 'watch_suggestion'
    | 'chat_handoff'
    // A draft the user marked done without running it
    | 'marked_done',
  status?: ActionCard['status'],
  completionConfirmation: 'queued' | 'server_terminal' = completionMethod ===
  'acknowledge'
    ? 'queued'
    : 'server_terminal'
): void =>
  trackEvent('ACTION_CARD_COMPLETED', {
    ...props,
    ...(status === undefined ? {} : { status }),
    completion_method: completionMethod,
    completion_confirmation: completionConfirmation,
  });

export const trackCardWatchSuggestion = (
  event:
    | 'CARD_WATCH_SUGGESTION_SHOWN'
    | 'CARD_WATCH_SUGGESTION_ACCEPTED'
    | 'CARD_WATCH_SUGGESTION_KEPT',
  card: ActionCard,
  suggestion: CardWatchSuggestion['kind'],
  surface: ActionCardSurface,
  context = actionCardAnalyticsContext(surface)
): void =>
  trackEvent(event, {
    ...card.analytics,
    ...buildActionCardProps(card, context),
    suggestion,
  });

export const trackCardAcknowledgedSnapshot = (
  props: ActionCardEventProps
): void => trackEvent('ACTION_CARD_ACKNOWLEDGED', props);

export const trackCardEditSavedSnapshot = (props: ActionCardEventProps): void =>
  trackEvent('ACTION_CARD_EDIT_SAVED', props);

export const trackCardSourceOpened = (
  card: ActionCard,
  surface: ActionCardSurface,
  context = actionCardAnalyticsContext(surface)
): void => trackCardEvent('ACTION_CARD_SOURCE_OPENED', card, context);
